import React from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import styles from 'styles/pages/NewSurveyOptions.module.css';
import Tabs from '@ax2/common/components/tab/Tabs';

import { RouterParams, NewSurveyParamType } from 'router/RouterParams';
import { redirect, getSearchParam } from 'helpers/router';
import DefaultPage from './DefaultPage';
import UUID from '@ax2/common/model/UUID';
import { ExistingSurveysList } from 'components/question-builders/ExistingSurveysList';
import { TemplateSurveysList } from 'components/question-builders/TemplateSurveysList';
import { useSurveyDispatchContext } from 'contexts/SurveyContext';
import {
  createSurveyFromTemplate,
  createSurveyFromExisting,
  getSurveys,
  createSurvey,
} from 'rest/makeSurveyClient';
import _ from 'lodash';
import PanelType from '@ax2/common/model/PanelType';
import { useErrorUpdateContext } from '@ax2/common/contexts/ErrorContext';
import { getErrorMessage } from '@ax2/common/helpers/error';
import { RestError } from '@ax2/common/rest/baseClient';

enum NewSurveyType {
  Template = 'Create a new survey from template',
  Existing = 'Create a new survey from existing',
  Scratch = 'Create a new survey from scratch',
}

type NewSurveyOptionsData = RouteComponentProps<RouterParams>;

const NewSurveyOptions: React.FC<NewSurveyOptionsData> = ({
  history,
  match,
}) => {
  const updateError = useErrorUpdateContext();
  const surveyType = getSurveyTypeFromRouterParam(match.params.newSurveyType);

  const handleTabChange = (surveyType: NewSurveyType) => {
    switch (surveyType) {
      case NewSurveyType.Template:
        redirect(
          '/newSurveyOptions/' + getRouterParamFromSurveyType(surveyType)
        );
        break;
      case NewSurveyType.Existing:
        redirect(
          '/newSurveyOptions/' + getRouterParamFromSurveyType(surveyType)
        );
        break;
      case NewSurveyType.Scratch:
        handleNewSurveyFromScratch();
    }
  };

  const surveyDispatch = useSurveyDispatchContext();
  const panelParam = getSearchParam('panel');
  const specialtyParam = getSearchParam('specialty');
  const panel = panelParam ? (panelParam as PanelType) : null;
  const specialty = specialtyParam ? Number(specialtyParam) : undefined;

  const handleNewSurveyFromTemplate = async (surveyTemplateId: UUID) => {
    try {
      if (panel) {
        const id = await createSurveyFromTemplate(
          panel,
          surveyTemplateId,
          specialty
        );
        processToSurveyBuilder(id);
      }
    } catch (e) {
      updateError({
        text: getErrorMessage(e as RestError),
      });
    }
  };

  const handleNewSurveyFromExisting = async (existingSurveyId: UUID) => {
    try {
      const id = await createSurveyFromExisting(existingSurveyId);
      processToSurveyBuilder(id);
    } catch (e) {
      updateError({
        text: getErrorMessage(e as RestError),
      });
    }
  };

  const handleNewSurveyFromScratch = () => {
    if (panelParam) {
      const panel = panelParam as PanelType;
      const specialty: number | undefined = specialtyParam
        ? Number(specialtyParam)
        : undefined;
      createSurvey(panel, specialty)
        .then(id => {
          processToSurveyBuilder(id);
        })
        .catch(e =>
          updateError({
            text: getErrorMessage(e as RestError),
          })
        );
    }
  };

  const processToSurveyBuilder = async (surveyId: UUID) => {
    try {
      const allSurveys = await getSurveys();
      const surveyToProcess = _.find(allSurveys, s => s.id === surveyId);
      if (surveyToProcess) {
        surveyDispatch({ type: 'new', survey: surveyToProcess });
        redirect('/surveyBuilder/screener', [
          {
            name: 'surveyId',
            value: surveyToProcess.id,
          },
        ]);
      }
    } catch (e) {
      updateError({
        text: getErrorMessage(e as RestError),
      });
    }
  };

  const surveyTemplates = panel ? (
    <TemplateSurveysList
      onSelect={handleNewSurveyFromTemplate}
      panel={panel}
      specialty={specialty}
    />
  ) : (
    <></>
  );

  const existingSurveys = panel ? (
    <ExistingSurveysList
      onSelect={handleNewSurveyFromExisting}
      panel={panel}
      specialty={specialty}
    />
  ) : (
    <></>
  );
  return (
    <DefaultPage
      header={{
        title: 'New Survey',
        subtitle: 'Choose how you would like to begin building your survey.',
        backAction: {
          text: 'BACK TO DASHBOARD',
          onCompletion: () => history.push('/dashboard'),
        },
      }}
      wideContent={true}
    >
      <div className={styles.container}>
        <Tabs
          activeTab={surveyType}
          onTabChange={t => handleTabChange(t as NewSurveyType)}
          tabs={[
            {
              name: NewSurveyType.Template,
              label: NewSurveyType.Template.toUpperCase(),
              content: surveyTemplates,
            },
            {
              name: NewSurveyType.Existing,
              label: NewSurveyType.Existing.toUpperCase(),
              content: existingSurveys,
            },
            {
              name: NewSurveyType.Scratch,
              label: NewSurveyType.Scratch.toUpperCase(),
            },
          ]}
        />
      </div>
    </DefaultPage>
  );
};

export default withRouter(NewSurveyOptions);

function getSurveyTypeFromRouterParam(
  type?: NewSurveyParamType
): NewSurveyType {
  switch (type) {
    case 'template':
      return NewSurveyType.Template;
    case 'existing':
      return NewSurveyType.Existing;
    default:
      return NewSurveyType.Template;
  }
}
function getRouterParamFromSurveyType(type: NewSurveyType): NewSurveyParamType {
  switch (type) {
    case NewSurveyType.Template:
      return 'template';
    case NewSurveyType.Existing:
      return 'existing';
    default:
      return 'template';
  }
}
